import type { StaffQuoteConflictCode } from "@lafayette/schemas";

import { describeStaffFailure, neutralForbidden, STAFF_WORDS } from "../failure-words";

// How the Payments screens tell a staff member what went wrong. Rules:
//   1. No id, key, amount, reason or provider text goes into a message. The API sends none either: a refusal holds a code.
//   2. A refusal for access (403) and a missing id (404) read the same: the page never says whether a payment exists.
//   3. A failure that may have run says so, and whether the SAME action can be sent again (the same Idempotency-Key makes the server
//      run it once). A refusal that cannot work again says what to do next: reconcile first, refund first, reload.

export type PaymentFailureKind =
  | "conflict"
  | "invalid"
  | "bad_request"
  | "forbidden"
  | "unauthenticated"
  | "rate_limited"
  | "unavailable"
  | "network"
  | "response"
  | "unknown";

export type PaymentFailure = Readonly<{
  kind: PaymentFailureKind;
  message: string;
  /** The same action may be sent again with the SAME idempotency key: it may or may not have run. */
  retryable: boolean;
  /** The page must read the payment again: what the user saw is out of date. */
  reload: boolean;
  /** For a refused quote command: the rule that refused it. */
  quoteCode: StaffQuoteConflictCode | null;
}>;

const NEUTRAL_PAYMENT_FORBIDDEN = neutralForbidden("this payment");

const quoteMessages: Readonly<Record<StaffQuoteConflictCode, string>> = {
  "payment_request.settlement_possible":
    "The customer may already have paid at the provider. Open the payment and reconcile it first. Nothing was changed.",
  "payment_quote.provider_evidence_stale":
    "The payment changed while the provider was being asked. Nothing was changed. Try again.",
  "payment_quote.open_payment_exists":
    "A payment from this quote is already paid or refunded. Refund it first. Nothing was changed.",
  "payment_quote.version_conflict":
    "The quote changed since you opened it. The page shows the latest. Nothing was changed.",
  "request.version_conflict":
    "The request changed since you opened it. The page shows the latest. Nothing was changed.",
  "payment_quote.withdrawn": "This quote was already replaced or withdrawn. Nothing was changed.",
  "payment_quote.expired": "This quote has expired. Record a new quote. Nothing was changed.",
  "payment_quote.request_not_in_progress":
    "Quotes and payment requests need a request that is in progress. Nothing was changed.",
  "payment_quote.validity_invalid":
    "The quote must stay valid for more than now and for at most 30 days. Nothing was changed.",
};

export type PaymentErrorContext = "read" | "reconcile" | "refund" | "quote";

export function describePaymentError(
  error: unknown,
  context: PaymentErrorContext = "read",
): PaymentFailure {
  const { api, kind, message, retryable, reload } = describeStaffFailure(error, {
    forbidden: NEUTRAL_PAYMENT_FORBIDDEN,
    conflict: ({ detail }) => {
      if (detail.quoteConflict !== null) return quoteMessages[detail.quoteConflict.code];
      return context === "refund"
        ? "The refund conflicts with the current state: the payment changed, refunds are not allowed for it now, or payments are switched off. Nothing was refunded. The page shows the latest."
        : context === "reconcile"
          ? "The payment changed, or payments are switched off. Nothing was changed. The page shows the latest."
          : "The payment changed, or the same action is still running. Nothing was changed by this attempt. The page shows the latest.";
    },
    unavailable:
      context === "refund"
        ? "The payment provider is not available right now. Nothing was refunded yet. Try again: the same refund is never sent twice."
        : context === "reconcile"
          ? "The payment provider is not available right now. Try the reconcile again in a moment."
          : STAFF_WORDS.unavailable,
  });
  return {
    kind,
    message,
    retryable,
    reload,
    quoteCode: kind === "conflict" ? (api.detail.quoteConflict?.code ?? null) : null,
  };
}
